import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API_BASE, pickErr } from "@/lib/api";
import { notifyError, notifySuccess } from "@/lib/notify";

function toAbsolute(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE}${url.replace(/^\/api/, "")}`;
}

const EMPTY_FORM = {
  name: "",
  address: "",
  principal_name: "",
  contact_email: "",
  contact_phone: "",
  website: "",
};

export default function SchoolProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [school, setSchool] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get(`/schools/${id}`);
      setSchool(data);
      setForm({
        name: data?.name || "",
        address: data?.address || "",
        principal_name: data?.principal_name || "",
        contact_email: data?.contact_email || "",
        contact_phone: data?.contact_phone || "",
        website: data?.website || "",
      });
    } catch (err) {
      setLoadError(pickErr(err, "Failed to load school"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  function onChange(key) {
    return (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  }

  async function onSave(e) {
    e.preventDefault();
    setSaveError("");
    setSaving(true);
    try {
      // Send only fields with values; backend treats unset as "no change".
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        const trimmed = typeof v === "string" ? v.trim() : v;
        if (trimmed !== "") payload[k] = trimmed;
      }
      const { data } = await api.put(`/schools/${id}`, payload);
      setSchool((prev) => ({ ...(prev || {}), ...data }));
      notifySuccess("School details saved");
    } catch (err) {
      const msg = pickErr(err, "Failed to save school");
      setSaveError(msg);
      notifyError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function onLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError("");
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/schools/${id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSchool((prev) => ({ ...(prev || {}), logo_url: data.logo_url }));
      notifySuccess("Logo uploaded");
    } catch (err) {
      const msg = pickErr(err, "Failed to upload logo");
      setLogoError(msg);
      notifyError(msg);
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Loading school…</p>;
  }
  if (loadError) {
    return (
      <div>
        <p data-testid="school-profile-load-error" className="text-sm text-red-600">
          {loadError}
        </p>
        <button
          onClick={() => navigate("/admin/schools")}
          className="mt-3 text-sm text-slate-600 hover:text-slate-900"
        >
          ← Back to schools
        </button>
      </div>
    );
  }
  if (!school) return null;

  const logoSrc = school.logo_url ? toAbsolute(school.logo_url) : "";

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-semibold text-slate-900">School Profile</h2>
        <button
          onClick={() => navigate("/admin/schools")}
          className="text-sm text-slate-600 hover:text-slate-900"
          data-testid="school-profile-back"
        >
          ← Back
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        View and update details for{" "}
        <span className="text-slate-700">{school.name}</span>.
      </p>

      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)] items-start">
        {/* --------- Logo card --------- */}
        <section
          className="bg-white border border-slate-200 rounded-lg p-5"
          data-testid="school-profile-logo-card"
        >
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Logo</h3>

          <div className="h-32 w-32 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden mb-3">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={`${school.name} logo`}
                className="max-h-full max-w-full object-contain"
                data-testid="school-profile-logo-preview"
              />
            ) : (
              <div
                className="flex h-full w-full items-center justify-center text-2xl font-bold text-indigo-600 bg-indigo-50"
                data-testid="school-profile-logo-fallback"
              >
                S
              </div>
            )}
          </div>

          <label className="block">
            <span className="text-xs text-slate-600">
              PNG, JPG, WEBP, GIF or SVG (max 2 MB)
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
              onChange={onLogoChange}
              disabled={logoBusy}
              data-testid="school-profile-logo-input"
              className="mt-2 block w-full text-xs text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-white hover:file:bg-slate-800 disabled:opacity-60"
            />
          </label>
          {logoBusy ? (
            <p className="mt-2 text-xs text-slate-500">Uploading…</p>
          ) : null}
          {logoError ? (
            <p
              data-testid="school-profile-logo-error"
              className="mt-2 text-xs text-red-600"
            >
              {logoError}
            </p>
          ) : null}
        </section>

        {/* --------- Details form --------- */}
        <section className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">
            Details
          </h3>
          <form
            onSubmit={onSave}
            className="grid sm:grid-cols-2 gap-4"
            data-testid="school-profile-form"
          >
            <Field label="Name" required>
              <input
                required
                value={form.name}
                onChange={onChange("name")}
                data-testid="school-profile-name"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Principal name">
              <input
                value={form.principal_name}
                onChange={onChange("principal_name")}
                data-testid="school-profile-principal"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Address" full>
              <input
                value={form.address}
                onChange={onChange("address")}
                data-testid="school-profile-address"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Contact email">
              <input
                type="email"
                value={form.contact_email}
                onChange={onChange("contact_email")}
                data-testid="school-profile-email"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Contact phone">
              <input
                value={form.contact_phone}
                onChange={onChange("contact_phone")}
                data-testid="school-profile-phone"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Website" full>
              <input
                type="url"
                value={form.website}
                onChange={onChange("website")}
                placeholder="https://"
                data-testid="school-profile-website"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </Field>

            <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-2">
              {saveError ? (
                <p
                  data-testid="school-profile-save-error"
                  className="text-sm text-red-600 mr-auto"
                >
                  {saveError}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={saving}
                data-testid="school-profile-save"
                className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>

          {/* --------- Stats summary --------- */}
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <Stat label="Students" value={school.total_students ?? 0} />
            <Stat
              label="Tests assigned"
              value={school.total_assigned_tests ?? 0}
            />
            <Stat
              label="Tests completed"
              value={school.total_completed_tests ?? 0}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, full, required, children }) {
  return (
    <label className={full ? "sm:col-span-2 block" : "block"}>
      <span className="block text-xs text-slate-600 mb-1">
        {label}
        {required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 py-3">
      <div className="text-lg font-semibold text-slate-900 tabular-nums">
        {value}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
